/**
 * SupabasePatternStore — `PatternStore` that mirrors local saves into
 * a Supabase `patterns` table so signed-in users see the same bank
 * across browsers/devices.
 *
 * Design tradeoff: the parent `PatternStore` exposes a synchronous
 * read API (`list()` / `get()`) that the sequencer + song panels
 * call directly during render. We can't change those signatures
 * without breaking every caller. So this subclass keeps the local
 * cache as the source of truth for reads — instant — and treats
 * Supabase as an *eventual* mirror:
 *
 *   - On construction + on every auth state change to "signed in",
 *     we kick off a background `hydrate()` that:
 *       1. Pulls every row from `patterns` (RLS-scoped to the user).
 *       2. Merges them into the local cache via the parent's
 *          `mergePatterns()` helper, taking the newer `updatedAt`
 *          on collisions.
 *       3. Pushes any local-only patterns up so a user who created
 *          patterns before signing in keeps them.
 *
 *   - On every successful local mutation (`put` / `update` / `delete`
 *     / `clear`), we fire-and-forget the corresponding Supabase
 *     write. UI doesn't block on the network; failures log and
 *     leave the local copy intact for the next hydrate to catch.
 *
 * On sign-out we leave the local cache alone — patterns persist in
 * localStorage as before, and the next sign-in's hydrate will fold
 * them back into the cloud copy.
 */

import type { SupabaseClient } from '@supabase/supabase-js';
import { PatternStore, type PatternData } from '@ac-slayer/synth-core';
import type { AuthManager } from '@/auth/AuthManager';

interface PatternRow {
  id: string;
  user_id: string;
  name: string;
  data: PatternData;
  updated_at: string;
}

export class SupabasePatternStore extends PatternStore {
  /**
   * Tracks whether a hydrate is in flight so concurrent auth events
   * (init + immediate session restore both fire) don't kick off
   * duplicate fetches.
   */
  private hydrating = false;
  private unsubscribeAuth: (() => void) | null = null;

  constructor(
    private readonly client: SupabaseClient,
    private readonly auth: AuthManager,
  ) {
    super();
    // Hydrate on every transition to signed-in. The auth manager
    // replays its current state immediately on subscribe, so this
    // also covers "page-load with an existing session" without a
    // separate priming call.
    this.unsubscribeAuth = this.auth.subscribe((state) => {
      if (state.kind === 'signed-in') {
        void this.hydrate();
      }
    });
  }

  dispose(): void {
    this.unsubscribeAuth?.();
    this.unsubscribeAuth = null;
  }

  // ── Hydrate ─────────────────────────────────────────────────────

  /**
   * Pull every pattern owned by the current user, merge into local
   * cache, then push any local-only patterns up. Safe to call
   * repeatedly; concurrent calls coalesce onto the in-flight one.
   *
   * Errors are logged, not thrown — patterns are not blocking work,
   * and the user shouldn't be served a modal because their cloud
   * couldn't reach the network. Local cache stays usable regardless.
   */
  async hydrate(): Promise<void> {
    if (this.hydrating) return;
    const userId = this.auth.currentUser?.id;
    if (!userId) return;
    this.hydrating = true;
    try {
      const { data, error } = await this.client
        .from('patterns')
        .select('data')
        .eq('user_id', userId)
        .order('updated_at', { ascending: false });
      if (error) {
        console.warn('[patterns] cloud fetch failed:', error.message);
        return;
      }
      const rows = (data ?? []) as Pick<PatternRow, 'data'>[];
      const cloud: PatternData[] = [];
      for (const r of rows) {
        if (isValidPattern(r.data)) cloud.push(r.data);
      }
      // 1. Pull cloud → local. Newer-wins merge.
      const localBefore = new Map(this.list().map((p) => [p.id, p]));
      this.mergePatterns(cloud);

      // 2. Push local-only → cloud. Anything in the pre-merge local
      //    set whose id is not in the cloud rows belongs only on
      //    this device; push it up so other devices see it. We do
      //    this AFTER the merge so any pattern that came down with
      //    a newer cloud version doesn't get clobbered by an older
      //    local copy.
      const cloudIds = new Set(cloud.map((p) => p.id));
      const localOnly: PatternData[] = [];
      for (const [id, p] of localBefore) {
        if (!cloudIds.has(id)) localOnly.push(p);
      }
      if (localOnly.length > 0) {
        await this.pushBatch(localOnly, userId);
      }
    } catch (err) {
      console.warn('[patterns] hydrate failed:', err);
    } finally {
      this.hydrating = false;
    }
  }

  // ── Write-through overrides ─────────────────────────────────────
  //
  // Each override calls super first (writes the local cache + emits
  // listeners synchronously, so the UI updates immediately), then
  // schedules a Supabase write. We fire-and-forget; on failure the
  // local copy stays and the next hydrate catches the inconsistency.

  override put(
    pattern: Parameters<PatternStore['put']>[0],
  ): string {
    const id = super.put(pattern);
    const created = this.get(id);
    if (created) this.scheduleUpsert(created);
    return id;
  }

  override update(
    id: string,
    patch: Parameters<PatternStore['update']>[1],
  ): boolean {
    const ok = super.update(id, patch);
    if (ok) {
      const updated = this.get(id);
      if (updated) this.scheduleUpsert(updated);
    }
    return ok;
  }

  override delete(id: string): boolean {
    const ok = super.delete(id);
    if (ok) this.scheduleDelete(id);
    return ok;
  }

  override clear(): void {
    const ids = this.list().map((p) => p.id);
    super.clear();
    for (const id of ids) this.scheduleDelete(id);
  }

  // ── Internal: cloud writes ──────────────────────────────────────

  private scheduleUpsert(pattern: PatternData): void {
    const userId = this.auth.currentUser?.id;
    if (!userId) return; // signed-out — local-only, will sync on next hydrate
    void this.upsertOne(pattern, userId);
  }

  private async upsertOne(pattern: PatternData, userId: string): Promise<void> {
    try {
      const { error } = await this.client.from('patterns').upsert({
        id: pattern.id,
        user_id: userId,
        name: pattern.name,
        data: pattern,
        updated_at: new Date(pattern.updatedAt).toISOString(),
      });
      if (error) console.warn('[patterns] cloud upsert failed:', error.message);
    } catch (err) {
      console.warn('[patterns] cloud upsert threw:', err);
    }
  }

  /** Bulk-push local patterns that don't exist in the cloud yet.
   *  Used during hydrate's "merge up" phase. Errors are logged
   *  per-row so one bad row doesn't abort the whole sync. */
  private async pushBatch(patterns: PatternData[], userId: string): Promise<void> {
    const rows = patterns.map((p) => ({
      id: p.id,
      user_id: userId,
      name: p.name,
      data: p,
      updated_at: new Date(p.updatedAt).toISOString(),
    }));
    const { error } = await this.client.from('patterns').upsert(rows);
    if (error) {
      console.warn('[patterns] cloud bulk push failed:', error.message);
    }
  }

  private scheduleDelete(id: string): void {
    const userId = this.auth.currentUser?.id;
    if (!userId) return;
    void this.deleteOne(id, userId);
  }

  private async deleteOne(id: string, userId: string): Promise<void> {
    try {
      const { error } = await this.client
        .from('patterns')
        .delete()
        .eq('id', id)
        .eq('user_id', userId);
      if (error) console.warn('[patterns] cloud delete failed:', error.message);
    } catch (err) {
      console.warn('[patterns] cloud delete threw:', err);
    }
  }
}

/** Same shape-check as PatternStore's internal validator. Duplicated
 *  here because the library doesn't export it; lift it later if more
 *  consumers need it. */
function isValidPattern(p: unknown): p is PatternData {
  if (!p || typeof p !== 'object') return false;
  const c = p as Record<string, unknown>;
  return (
    typeof c['id'] === 'string' &&
    typeof c['name'] === 'string' &&
    typeof c['createdAt'] === 'number' &&
    typeof c['updatedAt'] === 'number' &&
    typeof c['rate'] === 'string' &&
    typeof c['length'] === 'number' &&
    typeof c['swing'] === 'number' &&
    Array.isArray(c['steps'])
  );
}
